/**
 * App key status values as stored/returned by solutions-dashboard-service
 * (`app_keys.status`). The Go API uses lowercase, so the enum values match the
 * wire contract and can be sent directly on status-update requests.
 */
export enum AppKeyStatus {
  ENABLED = "enabled",
  DISABLED = "disabled",
}

/**
 * Coerces any raw status (legacy uppercase `ENABLED`, Go lowercase `enabled`,
 * undefined) to a canonical {@link AppKeyStatus} so UI comparisons stay simple.
 */
export function normalizeKeyStatus(status?: string): AppKeyStatus {
  return String(status).toUpperCase() === "ENABLED"
    ? AppKeyStatus.ENABLED
    : AppKeyStatus.DISABLED;
}

/**
 * App key fields from solutions-dashboard-service `GET /keys` (`AppKeyData`).
 * `api_key` is only returned on create; it is not stored or re-fetched.
 */
export type AppKey = {
  key_id: string;
  app_id: string;
  key_name: string;
  api_key?: string;
  status?: string;
  created_by?: string;
  updated_by?: string;
  is_active?: boolean;
  is_deleted?: boolean;
  created_at?: number;
  updated_at?: number;
};

/**
 * App fields from solutions-dashboard-service `GET /apps` (`AppData`).
 * List/create/update return `products_access` as product id strings.
 * GET by id returns catalog objects (same shape as GET /products).
 */
export type AppProductAccess = {
  product_pid: string;
  product_arn?: string;
  product_name?: string;
  category?: string;
  short_description?: string;
  additional_info?: string;
  document_link?: string;
  default_free_transactions?: number | null;
  is_approval_required?: boolean;
  display_on_dashboard?: boolean;
  display_on_trial?: boolean;
  addons?: unknown[];
  created_at?: string;
  updated_at?: string;
  subscription_status?: string;
  remaining_test_credits?: unknown;
};

export type App = {
  service_id?: string;
  app_id: string;
  app_name: string;
  organisation_id: string;
  test_app: boolean;
  status?: string;
  products_access: string[] | AppProductAccess[] | { _id: string; name: string }[];
  ip_whitelisted: string[];
  is_active?: boolean;
  is_deleted?: boolean;
  created_at?: number;
  updated_at?: number;
  keys?: AppKey[];
  /** Derived count for the apps grid; set from embedded `keys` when present. */
  keys_count?: number;
};
